import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { loginUser, getCurrentUser, refreshSession, logoutSession } from '../services/authService';
import { resetFinanceData } from '../services/discoveryService';
import { getOrgScope } from '../services/orgStructureService';
import { resetSessionExpiredFlag } from '../services/api';
import { getTenantSlugFromUrl, tenantUrl, isValidSlug } from '../utils/tenantPath';
import { isSingleTenant } from '../services/deploymentConfigService';

const AuthContext = createContext();

const AUTH_EXPIRED_EVENT = 'auth:expired';

const TOKEN_KEY = 'authToken';
const TOKEN_TYPE_KEY = 'tokenType';
const ROLE_KEY = 'ee_assistant_role';
const EMAIL_KEY = 'ee_assistant_email';
const PROFILE_KEY = 'ee_assistant_profile';
const USER_DATA_KEY = 'ee_assistant_user_data';

const BASE64_BLOCK_LEN = 4;
const HTTP_UNAUTHORIZED = 401;
// Renew the access token this long before it lapses, but never sooner than the floor.
const RENEW_LEAD_MS = 120_000;
const RENEW_MIN_DELAY_MS = 5_000;
// Below this much remaining validity the exp timer is skipped (clock-skew guard).
const MIN_EXPIRY_TIMER_MS = 60_000;

// Minimal JWT payload decoder. Returns null on any failure — never throws.
// We only read non-sensitive identity claims; signature is validated by
// the API on every request.
const decodeJwtPayload = (token) => {
  if (!token || typeof token !== 'string') {return null;}
  const parts = token.split('.');
  if (parts.length < 2) {return null;}
  try {
    const b64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const padded = b64 + '==='.slice((b64.length + BASE64_BLOCK_LEN - 1) % BASE64_BLOCK_LEN);
    return JSON.parse(atob(padded));
  } catch {
    return null;
  }
};

// A superuser is always 'admin'; otherwise use the stored role (defaulting
// to 'User'), lower-cased to match the rest of the app's role checks.
export const deriveRoleFromUser = (user) => (user.is_superuser ? 'admin' : (user.role || 'User').toLowerCase());

// Prefer the org-position profile the server just returned; fall back to
// whatever profile is cached in localStorage (as a JSON string) under
// PROFILE_KEY, e.g. across an optimistic-restore where the server hasn't
// answered yet.
export const resolveOrgProfile = (orgProfile, cachedProfileJson) => {
  if (orgProfile) {return orgProfile;}
  return cachedProfileJson ? JSON.parse(cachedProfileJson) : null;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {throw new Error('useAuth must be used within AuthProvider');}
  return context;
};

export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [userRole, setUserRole] = useState(null);
  const [userEmail, setUserEmail] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [userData, setUserData] = useState(null);
  const [tenantKind, setTenantKind] = useState('customer');
  // Org-position permission scope (visible/editable units). Drives the AI
  // Finance menu, the Org Structure page, and edit-control gating. Null until
  // loaded; the backend independently enforces the same rules.
  const [orgScope, setOrgScope] = useState(null);
  const [authError, setAuthError] = useState('');
  const [isLoading, setIsLoading] = useState(true); // start true — checking token
  // Distinct from isLoading: true ONLY during the initial bootstrap
  // token-validation, and never toggled by an interactive login(). The
  // full-screen bootstrap spinner keys off this so an in-progress login does
  // not unmount AccessGate (which would drop its local modal/form state — see
  // the "Email Not Verified" modal flow). Interactive login uses isLoading,
  // which drives the Sign-In button's inline "Signing in…" state.
  const [isInitializing, setIsInitializing] = useState(true);
  // Session-expired surface. Set to true when any API call returns 401.
  // SessionExpiredModal listens to this and overlays the app.
  const [sessionExpired, setSessionExpired] = useState(false);
  // Bumped whenever the access token is silently renewed, so the expiry
  // timers below re-arm against the new `exp`.
  const [tokenVersion, setTokenVersion] = useState(0);

  // Validate existing token on mount.
  //
  // Optimistic bootstrap: if we have cached user data + token, render the app
  // immediately using cached data and revalidate /auth/me in the background.
  // This prevents the UI from hanging on a blank spinner if /auth/me is slow
  // or temporarily unreachable.
  useEffect(() => {
    // ── Entra SSO callback handler ─────────────────────────────────────
    // The backend redirects here with `#sso_token=<jwt>&next=<path>` on
    // success, or `#sso_error=<code>` on failure. Pluck the token from
    // the URL fragment, store it, then strip the fragment so the user
    // never sees it in the address bar.
    let ssoTokenFromHash = null;
    let ssoNextPath = null;
    let ssoErrorCode = null;
    try {
      const hash = window.location.hash || '';
      if (hash.length > 1) {
        const params = new URLSearchParams(hash.slice(1));
        ssoTokenFromHash = params.get('sso_token');
        ssoNextPath = params.get('next');
        ssoErrorCode = params.get('sso_error');
        if (ssoTokenFromHash || ssoErrorCode) {
          // Clean the URL — replace state without triggering a reload.
          window.history.replaceState(
            null,
            '',
            window.location.pathname + window.location.search,
          );
        }
      }
    } catch { /* ignore malformed hash */ }

    if (ssoErrorCode) {
      setAuthError(`SSO_ERROR:${ssoErrorCode}`);
    }

    if (ssoTokenFromHash) {
      // Persist the token immediately so the rest of the bootstrap
      // treats it as a normal authenticated session.
      localStorage.setItem(TOKEN_KEY, ssoTokenFromHash);
      localStorage.setItem(TOKEN_TYPE_KEY, 'bearer');
      getCurrentUser(ssoTokenFromHash)
        .then((user) => {
          const orgProfile = user.org_profile || null;
          _setAuthState(ssoTokenFromHash, user, orgProfile);
          if (ssoNextPath && ssoNextPath !== '/' && ssoNextPath !== window.location.pathname) {
            try { window.history.replaceState(null, '', ssoNextPath); } catch { /* ignore */ }
          }
        })
        .catch((err) => {
          // Only treat 401 as a definitive "SSO token rejected". Network
          // errors should leave whatever state we had alone and let a real
          // API call fire `auth:expired` if the session is actually dead.
          if (err && err.status === HTTP_UNAUTHORIZED) {_clearAuth();}
        })
        .finally(() => { setIsLoading(false); setIsInitializing(false); });
      return;
    }

    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) {
      setIsLoading(false);
      setIsInitializing(false);
      return;
    }

    // Try optimistic restore from cached user data
    let optimisticOk = false;
    try {
      const cachedUserRaw = localStorage.getItem(USER_DATA_KEY);
      if (cachedUserRaw) {
        const cachedUser = JSON.parse(cachedUserRaw);
        const cachedProfile = localStorage.getItem(PROFILE_KEY)
          ? JSON.parse(localStorage.getItem(PROFILE_KEY))
          : null;
        _setAuthState(token, cachedUser, cachedProfile);
        setIsLoading(false);
        setIsInitializing(false);
        optimisticOk = true;
      }
    } catch {
      /* fall through to network validation */
    }

    // Always revalidate against the server (background if optimistic).
    // ONLY clear auth when the server explicitly says 401. A network error,
    // aborted fetch (e.g. the tenant-prefix redirect cancels the in-flight
    // request), or transient 5xx must not wipe the optimistic auth state —
    // otherwise a deep-link landing race wipes localStorage right before
    // the redirect lands, leaving the next page unauthenticated.
    getCurrentUser(token)
      .then((user) => {
        // Someone else signed in while this check was in flight: writing back
        // the token captured at mount would sign them out as the previous user.
        if (localStorage.getItem(TOKEN_KEY) !== token) {return;}
        const orgProfile = user.org_profile ||
          (localStorage.getItem(PROFILE_KEY) ? JSON.parse(localStorage.getItem(PROFILE_KEY)) : null);
        _setAuthState(token, user, orgProfile);
      })
      .catch((err) => {
        // Only the token this check was about may be cleared.
        if (err && err.status === HTTP_UNAUTHORIZED && localStorage.getItem(TOKEN_KEY) === token) {
          _clearAuth();
        }
        // For status 0 (network / aborted) or any non-401 error, leave the
        // optimistic state alone. A subsequent real request that hits 401
        // will fire `auth:expired` and surface the session-expired modal.
      })
      .finally(() => {
        if (!optimisticOk) {setIsLoading(false);}
        setIsInitializing(false);
      });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const _setAuthState = (token, user, orgProfile) => {
    localStorage.setItem(TOKEN_KEY, token);
    localStorage.setItem(TOKEN_TYPE_KEY, 'bearer');

    const role = deriveRoleFromUser(user);
    const email = user.email || '';

    // A different user signing in on this tab must not inherit the previous
    // user's module-global finance/roster cache (single-tenant login is a soft
    // SPA transition, no page reload).
    if (email && email !== userEmail) {resetFinanceData();}

    localStorage.setItem(ROLE_KEY, role);
    localStorage.setItem(EMAIL_KEY, email);
    localStorage.setItem(USER_DATA_KEY, JSON.stringify(user));
    if (orgProfile) {localStorage.setItem(PROFILE_KEY, JSON.stringify(orgProfile));}

    setIsAuthenticated(true);
    setUserRole(role);
    setUserEmail(email);
    setUserData(user);
    setUserProfile(resolveOrgProfile(orgProfile, localStorage.getItem(PROFILE_KEY)));
    setAuthError('');

    // Decode tenant_kind from JWT claims (set by tenant-api at login time).
    // Falls back to 'customer' so the Studio banner stays hidden by default.
    const claims = decodeJwtPayload(token) || {};
    setTenantKind(claims.tenant_kind || 'customer');

    // Fetch the org-position scope for menu / page gating. Best-effort: on
    // failure leave it null so the UI degrades to role-only gating (the
    // backend still enforces scope on every request).
    getOrgScope()
      .then((s) => setOrgScope(s))
      .catch(() => setOrgScope(null));
  };

  const _clearAuth = () => {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(TOKEN_TYPE_KEY);
    localStorage.removeItem(ROLE_KEY);
    localStorage.removeItem(EMAIL_KEY);
    localStorage.removeItem(PROFILE_KEY);
    localStorage.removeItem(USER_DATA_KEY);
    // Wipe the module-global finance/roster cache so the next user on this tab
    // can't see the previous user's data (single-tenant login does not reload).
    resetFinanceData();
    setIsAuthenticated(false);
    setUserRole(null);
    setUserEmail(null);
    setUserProfile(null);
    setUserData(null);
    setTenantKind('customer');
    setOrgScope(null);
  };

  /**
   * Login with username + password (+ optional workspace/org slug) → JWT flow.
   *
   * When the URL path doesn't already encode the tenant slug, we do a
   * full-page redirect to ``/<slug>/`` after auth so react-router
   * remounts with the right basename.
   */
  const login = async (username, password, organization) => {
    setAuthError('');
    setIsLoading(true);
    try {
      const data = await loginUser(username, password, organization);
      const token = data.access_token;

      // Fetch full user object via /auth/me
      const user = await getCurrentUser(token);

      // Resolve the slug the token is scoped to (server-provided, falls
      // back to JWT claim). Prefer the server's `organization_id` field
      // since it's authoritative.
      const claims = (() => {
        try {
          const parts = token.split('.');
          const padded = parts[1].replace(/-/g, '+').replace(/_/g, '/');
          const b64 = padded + '==='.slice((padded.length + BASE64_BLOCK_LEN - 1) % BASE64_BLOCK_LEN);
          return JSON.parse(atob(b64));
        } catch { return {}; }
      })();
      const tokenSlug = (data.organization_id || claims.organization_id || '').toString();
      const urlSlug = getTenantSlugFromUrl();

      // Persist auth state BEFORE redirecting so the post-redirect boot
      // restores the session from localStorage.
      _setAuthState(token, user, data.org_profile);

      // Single-tenant: URLs are slug-less by design, so don't redirect to
      // a slug-prefixed URL after login (would loop with the URL-path
      // enforcement effect below).
      if (!isSingleTenant() && isValidSlug(tokenSlug) && tokenSlug !== urlSlug) {
        // Full-page nav so <BrowserRouter basename> remounts.
        window.location.assign(tenantUrl(tokenSlug, '/'));
        return { success: true, redirected: true };
      }
      return { success: true };
    } catch (err) {
      const detail = err.detail || err.message || 'Login failed';
      setAuthError(detail);
      return { success: false, error: detail };
    } finally {
      setIsLoading(false);
    }
  };

  const logout = useCallback(() => {
    logoutSession();
    _clearAuth();
    setAuthError('');
  }, []);

  // ── Session-expiry handling ────────────────────────────────────────
  // Three triggers feed the same `sessionExpired` flag:
  //   1. Any API call returning 401 → api.js dispatches 'auth:expired'.
  //   2. Cross-tab logout — another tab clears localStorage.
  //   3. JWT `exp` claim elapsing while tab is open (proactive timer).
  useEffect(() => {
    const onExpired = () => {
      if (localStorage.getItem('authToken')) {
        setSessionExpired(true);
      }
    };
    const onStorage = (e) => {
      if (e.key === 'authToken' && !e.newValue && isAuthenticated) {
        setSessionExpired(true);
      }
      if (e.key === 'authToken' && e.newValue && !isAuthenticated) {
        window.location.reload();
      }
    };
    window.addEventListener(AUTH_EXPIRED_EVENT, onExpired);
    window.addEventListener('storage', onStorage);
    return () => {
      window.removeEventListener(AUTH_EXPIRED_EVENT, onExpired);
      window.removeEventListener('storage', onStorage);
    };
  }, [isAuthenticated]);

  // Silent renewal. Fires shortly before the short-lived access token lapses
  // and swaps it using the rotating HttpOnly refresh cookie, so shortening the
  // access-token TTL doesn't bounce people back to the login page.
  useEffect(() => {
    if (!isAuthenticated) {return undefined;}
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) {return undefined;}
    let expMs = 0;
    try {
      const payload = JSON.parse(atob(token.split('.')[1] || ''));
      if (payload && typeof payload.exp === 'number') {expMs = payload.exp * 1000;}
    } catch { /* malformed token — the 401 path handles it */ }
    if (!expMs) {return undefined;}
    const delay = Math.max(expMs - Date.now() - RENEW_LEAD_MS, RENEW_MIN_DELAY_MS);
    const timer = setTimeout(async () => {
      const data = await refreshSession();
      if (data && data.access_token) {
        localStorage.setItem(TOKEN_KEY, data.access_token);
        setTokenVersion((v) => v + 1);
      }
    }, delay);
    return () => clearTimeout(timer);
  }, [isAuthenticated, tokenVersion]);

  // Proactive exp-claim timer.
  useEffect(() => {
    if (!isAuthenticated) {return undefined;}
    const token = localStorage.getItem('authToken');
    if (!token) {return undefined;}
    let expMs = 0;
    try {
      const payload = JSON.parse(atob(token.split('.')[1] || ''));
      if (payload && typeof payload.exp === 'number') {expMs = payload.exp * 1000;}
    } catch { /* malformed token — let API 401s catch it */ }
    if (!expMs) {return undefined;}
    const delay = expMs - Date.now();
    // Guard against client/server clock skew. A freshly-issued token
    // should always have many minutes of validity left; if the local
    // clock thinks the token is already (or nearly) expired, we trust
    // the server's actual JWT validation over the local clock and let
    // a real 401 from the next API call be the canonical signal.
    // Without this guard, a forward-skewed client clock fires
    // `auth:expired` immediately after login and pops the modal
    // straight after sign-in.
    if (delay < MIN_EXPIRY_TIMER_MS) {return undefined;}
    const timer = setTimeout(() => {
      window.dispatchEvent(new CustomEvent(AUTH_EXPIRED_EVENT, {
        detail: { resumeUrl: window.location.pathname + window.location.search },
      }));
    }, delay);
    return () => clearTimeout(timer);
  }, [isAuthenticated, tokenVersion]);

  // Called by SessionExpiredModal on successful re-login.
  const dismissSessionExpired = useCallback(() => {
    setSessionExpired(false);
    try { resetSessionExpiredFlag(); } catch { /* ignore */ }
  }, []);

  // ── Tenant URL-path enforcement ────────────────────────────────────
  // Whenever the user is authenticated, make sure the URL begins with
  // the tenant slug from the JWT. On mismatch / missing slug we do a
  // full-page navigation so the router remounts with the right
  // basename. Done with a one-shot ref so we never loop on the
  // ``replace()`` itself.
  //
  // SINGLE-TENANT MODE: URLs are intentionally slug-less. Running this
  // check would loop forever because getTenantSlugFromUrl() returns null
  // and never matches the JWT's tokenSlug.
  useEffect(() => {
    if (isSingleTenant()) {return;}
    if (!isAuthenticated) {return;}
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) {return;}
    let tokenSlug = '';
    try {
      const parts = token.split('.');
      const padded = parts[1].replace(/-/g, '+').replace(/_/g, '/');
      const b64 = padded + '==='.slice((padded.length + BASE64_BLOCK_LEN - 1) % BASE64_BLOCK_LEN);
      const claims = JSON.parse(atob(b64));
      tokenSlug = (claims.organization_id || '').toString();
    } catch { /* ignore — let API 401s catch it */ }
    if (!isValidSlug(tokenSlug)) {return;}
    const urlSlug = getTenantSlugFromUrl();
    if (urlSlug === tokenSlug) {return;}
    // Carry the user back to whatever sub-path they were trying to
    // visit, just rooted under the right tenant slug.
    const restPath = urlSlug
      ? (window.location.pathname.replace(`/${urlSlug}`, '') || '/')
      : (window.location.pathname || '/');
    window.location.replace(tenantUrl(tokenSlug, restPath + window.location.search));
  }, [isAuthenticated]);

  const refreshUser = async () => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) {return;}
    try {
      const user = await getCurrentUser(token);
      const orgProfile = user.org_profile ||
        (localStorage.getItem(PROFILE_KEY) ? JSON.parse(localStorage.getItem(PROFILE_KEY)) : null);
      _setAuthState(token, user, orgProfile);
    } catch (err) {
      // Only clear auth on a confirmed 401 from the server. Network /
      // aborted-fetch errors must not wipe the session — they could be
      // transient (server bounce, page navigating) and the real signal
      // will arrive via `auth:expired` on the next 401.
      if (err && err.status === HTTP_UNAUTHORIZED) {_clearAuth();}
    }
  };

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated,
        userRole,
        userEmail,
        userProfile,
        userData,
        tenantKind,
        orgScope,
        // Manager = admin or leads ≥1 org unit (can edit groups). Member = admin
        // or has any org row (can see the Org Structure page).
        isOrgManager: !!(orgScope && (orgScope.isAdmin || (orgScope.editableUnitIds || []).length > 0)),
        isOrgMember: !!(orgScope && (orgScope.isAdmin || orgScope.isMember)),
        isTemplateWorkspace: tenantKind === 'template_workspace',
        isAdmin: userRole === 'admin',
        login,
        logout,
        refreshUser,
        authError,
        isLoading,
        isInitializing,
        sessionExpired,
        dismissSessionExpired,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
